/**
 * TaskMarkerPicker — Wiederverwendbares UI-Element für Aufgaben-Edit.
 *
 * Lässt den User einen Plan auswählen und per Klick auf dem Plan-Preview
 * einen Standort-Pin (x_percent / y_percent) setzen.
 *
 * Props:
 *   projectId: string
 *   marker: { plan_id, page, x_percent, y_percent } | null
 *   onChange: (marker | null) => void
 *
 * Stil: passt sich an helle (Desktop) und dunkle (Mobile) Umgebungen über `theme="light" | "dark"` an.
 */
import { useEffect, useState, useCallback } from "react";
import { authFetch } from "../App";
import { MapPin, Map as MapIcon, X, Layers } from "lucide-react";
import PlanViewer from "./PlanViewer";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function TaskMarkerPicker({ projectId, marker, onChange, theme = "light" }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(marker?.plan_id || "");
  const [page, setPage] = useState(marker?.page || 1);
  const [currentPin, setCurrentPin] = useState(
    marker?.x_percent != null
      ? { x_percent: marker.x_percent, y_percent: marker.y_percent }
      : null
  );

  const isDark = theme === "dark";

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/projects/${projectId}/plans`);
      if (r.ok) {
        const d = await r.json();
        // Nur preview-fähige Pläne als Picker-Surface anbieten
        setPlans((d.plans || []).filter(p => p.has_preview));
      }
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { if (showPicker) loadPlans(); }, [showPicker, loadPlans]);

  // Wenn marker von außen geändert wird (z.B. Reset beim Tab-Wechsel)
  useEffect(() => {
    setSelectedPlanId(marker?.plan_id || "");
    setPage(marker?.page || 1);
    setCurrentPin(marker?.x_percent != null ? { x_percent: marker.x_percent, y_percent: marker.y_percent } : null);
  }, [marker?.plan_id, marker?.x_percent, marker?.y_percent, marker?.page]);

  const selectedPlan = plans.find(p => p.id === selectedPlanId);
  const hasMarker = !!(marker && marker.plan_id && marker.x_percent != null);

  const handlePick = ({ x_percent, y_percent, page: pg }) => {
    setCurrentPin({ x_percent, y_percent });
    if (selectedPlanId) {
      onChange({ plan_id: selectedPlanId, page: pg || page, x_percent, y_percent });
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlanId(planId);
    setCurrentPin(null);
    onChange(null); // Marker zurücksetzen, bis neu geklickt
  };

  const handleClear = () => {
    setCurrentPin(null);
    setSelectedPlanId("");
    onChange(null);
  };

  // ── Kompakte Anzeige außerhalb (Closed State) ──
  if (!showPicker) {
    return (
      <div className={`rounded-md border ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"} p-2.5`} data-testid="task-marker-picker-closed">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <MapPin className={`w-4 h-4 ${hasMarker ? "text-blue-500" : isDark ? "text-gray-500" : "text-gray-400"}`} />
            <div className="min-w-0">
              <p className={`text-xs ${isDark ? "text-gray-300" : "text-gray-700"} font-medium`}>
                {hasMarker ? "Standort markiert" : "Kein Plan-Marker"}
              </p>
              {hasMarker && marker && (
                <p className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"} truncate`}>
                  Pos. {marker.x_percent.toFixed(1)}% / {marker.y_percent.toFixed(1)}%
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {hasMarker && (
              <button
                type="button"
                onClick={handleClear}
                className={`p-1.5 rounded ${isDark ? "text-gray-400 hover:bg-white/10" : "text-gray-500 hover:bg-gray-200"}`}
                title="Marker entfernen"
                data-testid="task-marker-clear-btn"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className={`px-2.5 py-1 rounded text-[11px] font-medium ${isDark ? "bg-blue-600/20 text-blue-300 hover:bg-blue-600/30" : "bg-blue-100 text-blue-700 hover:bg-blue-200"}`}
              data-testid="task-marker-open-btn"
            >
              {hasMarker ? "Ändern" : "Auf Plan markieren"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Aufgeklappter Picker ──
  return (
    <div className={`rounded-md border ${isDark ? "border-blue-500/30 bg-white/5" : "border-blue-300 bg-blue-50/40"} p-3 space-y-2`} data-testid="task-marker-picker-open">
      <div className="flex items-center justify-between">
        <h4 className={`text-xs font-semibold ${isDark ? "text-gray-200" : "text-gray-800"} uppercase tracking-wider`}>
          Standort auf Plan markieren
        </h4>
        <button
          type="button"
          onClick={() => setShowPicker(false)}
          className={`p-1 rounded ${isDark ? "text-gray-400 hover:bg-white/10" : "text-gray-500 hover:bg-gray-200"}`}
          data-testid="task-marker-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {loading && <p className="text-xs text-gray-500">Lädt Pläne…</p>}
      {!loading && plans.length === 0 && (
        <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"} flex items-center gap-1.5`}>
          <MapIcon className="w-3.5 h-3.5" />
          Keine Pläne mit Vorschau verfügbar. Lade unter &bdquo;Pläne&ldquo; einen PDF/Bild-Plan hoch.
        </p>
      )}

      {plans.length > 0 && (
        <>
          <select
            value={selectedPlanId}
            onChange={(e) => handlePlanSelect(e.target.value)}
            className={`w-full h-9 px-2 rounded-md text-sm ${isDark ? "bg-white/5 border border-white/10 text-white" : "bg-white border border-gray-300 text-gray-900"}`}
            data-testid="task-marker-plan-select"
          >
            <option value="">— Plan wählen —</option>
            {plans.map(p => (
              <option key={p.id} value={p.id}>
                {p.title}{p.floor ? ` · ${p.floor}` : ""} ({p.file_type.toUpperCase()})
              </option>
            ))}
          </select>

          {selectedPlan && selectedPlan.page_count > 1 && (
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <Layers className="w-3.5 h-3.5 text-gray-500" />
              <span className={isDark ? "text-gray-400" : "text-gray-600"}>Seite:</span>
              {Array.from({ length: selectedPlan.page_count }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => { setPage(i + 1); setCurrentPin(null); onChange(null); }}
                  className={`px-2 py-0.5 rounded ${page === i + 1 ? "bg-blue-500 text-white" : isDark ? "bg-white/5 text-gray-300 hover:bg-white/10" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                  data-testid={`task-marker-page-${i + 1}`}
                >
                  {i + 1}
                </button>
              ))}
              <span className={`ml-1 text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                ({selectedPlan.page_count} Seiten · Seitenwechsel setzt Pin zurück)
              </span>
            </div>
          )}

          {selectedPlan && (
            <div className="relative">
              <PlanViewer
                plan={selectedPlan}
                src={`${API}/projects/${projectId}/plans/${selectedPlan.id}/file`}
                srcPreview={`${API}/projects/${projectId}/plans/${selectedPlan.id}/preview?page=${page}`}
                mode="picker"
                page={page}
                currentPin={currentPin}
                onPickLocation={handlePick}
                compact
              />
            </div>
          )}

          {currentPin && (
            <div className={`text-[11px] flex items-center gap-2 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              <MapPin className="w-3.5 h-3.5" />
              Standort gesetzt bei {currentPin.x_percent.toFixed(1)}% / {currentPin.y_percent.toFixed(1)}%. Klicke erneut zum Verschieben.
            </div>
          )}
        </>
      )}
    </div>
  );
}
